import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { getValidAccessToken, logout, showAlertOnce, hasStoredAuthUser } from "./Auth";

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let interceptorId: number | null = null;

/**
 * Attaches a global Axios response interceptor that catches 401 Unauthorized errors.
 * - Attempts to refresh the Cognito session transparently via getValidAccessToken().
 * - If refreshed successfully, updates the Authorization header and retries the original request.
 * - If refresh fails (or the request was already retried), logs out the user and displays
 *   a session expiration alert.
 */
export function setupAxiosInterceptors(): void {
  if (interceptorId !== null || !axios?.interceptors?.response) {
    return;
  }

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error?.config as CustomAxiosRequestConfig | undefined;

      if (error?.response?.status === 401 && originalRequest) {
        if (!originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Attempt to get a fresh token (calling without args forces refresh check via Amplify)
            const freshToken = await getValidAccessToken();

            if (freshToken) {
              if (!originalRequest.headers) {
                originalRequest.headers = {} as any;
              }
              if (typeof (originalRequest.headers as any).set === "function") {
                (originalRequest.headers as any).set("Authorization", `Bearer ${freshToken}`);
              } else {
                (originalRequest.headers as any)["Authorization"] = `Bearer ${freshToken}`;
              }
              return axios(originalRequest);
            }
          } catch (refreshErr) {
            console.error("[AxiosInterceptor] Token refresh failed:", refreshErr);
          }
        }

        // Either retry failed or session could not be refreshed
        if (hasStoredAuthUser()) {
          logout();
          showAlertOnce();
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Removes the global Axios response interceptor (primarily used for cleanup in tests).
 */
export function ejectAxiosInterceptors(): void {
  if (interceptorId !== null && axios?.interceptors?.response) {
    axios.interceptors.response.eject(interceptorId);
    interceptorId = null;
  }
}
